<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractServiceFees',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    service: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mapFees = (...args) => {
    return _.chain(args[0])
      .orderBy(['valid_from', 'valid_to'], ['desc', 'desc'])
      .map((item) => item)
      .value();
  };

  const fees = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    fees: fees.value,
    ux: ux.value,
  }));

  const $$load_fees = () => {
    return mapFees(props.service.fees);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'fees' }, { name: 'ux' }],
    loaders: { fees: $$load_fees },
    ux: {},
    dataRefs: { fees, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6705964765214521_emit_fjep7jo5c_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_6705964765214521_close_0ef8jbd7f_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_6705964765214521_showModal_w3hkyq3nx_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'editServiceFee',
        contract: props.contract,
        service: _.cloneDeep(props.service),
        fee: _.cloneDeep($event.scope.fee),
      },
    };
  };
  const act_96700093353179_emit_2chg8cq8b_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_96700093353179_close_vyde2y9te_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_96700093353179_showModal_9cubmrol6_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'addServiceFee',
        contract: props.contract,
        service: _.cloneDeep(props.service),
      },
    };
  };

  const act_6705964765214521_emit_fjep7jo5c = new actionTypes.EmitAction({
    actionArgs: act_6705964765214521_emit_fjep7jo5c_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_6705964765214521_close_0ef8jbd7f =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_emit_fjep7jo5c,
      event: 'close',
      when: inst_evh_6705964765214521_close_0ef8jbd7f_getWhen,
      displayName: 'emitReload',
    });
  const act_6705964765214521_showModal_w3hkyq3nx =
    new actionTypes.ShowModalAction({
      actionArgs: act_6705964765214521_showModal_w3hkyq3nx_getActionArgs,
      events: [inst_evh_6705964765214521_close_0ef8jbd7f],
    });
  const inst_evh_6705964765214521_clickPreventStop_mttjabnwg =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_showModal_w3hkyq3nx,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_mttjabnwg],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_emit_2chg8cq8b = new actionTypes.EmitAction({
    actionArgs: act_96700093353179_emit_2chg8cq8b_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_96700093353179_close_vyde2y9te =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_emit_2chg8cq8b,
      event: 'close',
      when: inst_evh_96700093353179_close_vyde2y9te_getWhen,
      displayName: 'emitReload',
    });
  const act_96700093353179_showModal_9cubmrol6 =
    new actionTypes.ShowModalAction({
      actionArgs: act_96700093353179_showModal_9cubmrol6_getActionArgs,
      events: [inst_evh_96700093353179_close_vyde2y9te],
    });
  const inst_evh_96700093353179_clickPreventStop_dpg5aq72c =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_showModal_9cubmrol6,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_dpg5aq72c],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_6705964765214521_close_0ef8jbd7f,
    emitReload: inst_evh_6705964765214521_close_0ef8jbd7f,
    inst_evh_6705964765214521_clickPreventStop_mttjabnwg,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_96700093353179_close_vyde2y9te,
    inst_evh_96700093353179_clickPreventStop_dpg5aq72c,
    inst_evh_96700093353179_clickPreventStop,
  };

  const evh_6705964765214521_close_0ef8jbd7f = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_close_0ef8jbd7f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_mttjabnwg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_mttjabnwg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_close_vyde2y9te = async (event, scope) => {
    return await actions.inst_evh_96700093353179_close_vyde2y9te.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_dpg5aq72c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_dpg5aq72c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BListGroup flush>
    <BListGroupItem :key="fee.id" v-for="fee in fees">
      <ItpBox :gap="2">
        <span> • </span>
        <ItpBox>
          <ItpText>
            <ItpText>{{ date(fee.valid_from) }}</ItpText>
            <ItpText v-if="fee.valid_to"> - {{ date(fee.valid_to) }}</ItpText>

            <ItpText class="px-3">
              <span v-if="fee.quantity != null">
                {{ number(fee.quantity) }}
                {{ fee.unit_of_quantity || '' }} &times;
              </span>
              {{ number(fee.amount) }} {{ fee.currency }} [{{ fee.unit }}]
            </ItpText>
          </ItpText>
          <ItpText :text="fee.notes" pre v-if="fee.notes"> </ItpText>
        </ItpBox>
        <ItpButton
          icon="fa-pen"
          :tooltip="gettext('Edit')"
          variant="light"
          size="sm"
          v-if="!readonly"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, { fee })
          "
        >
        </ItpButton>
      </ItpBox>
    </BListGroupItem>
  </BListGroup>
  <ItpBox class="ps-4 mt-1">
    <ItpButton
      :text="pgettext('Contract', 'Add service fee...')"
      variant="light"
      size="sm"
      v-if="!readonly"
      @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
</template>
